import { Check, X } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "./common";
import { resolveMedia } from "../../lib/api";

const DEFAULT_FOUNDER_IMAGE = "https://images.unsplash.com/photo-1584940120505-117038d90b05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

/* THE FOUNDER */
export function Founder({ data }) {
  const d = data || {};
  return (
    <section id="founder" data-testid="section-founder" className="bg-sand py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>The Founder</SectionLabel>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl">
              <img src={resolveMedia(d.image || DEFAULT_FOUNDER_IMAGE)} alt={d.name || "PARESH TRIVEDI"} loading="lazy" className="aspect-[4/5] w-full object-cover" data-testid="founder-image" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy to-transparent p-6">
                <p className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">{d.name}</p>
                <p className="font-body text-sm font-semibold uppercase tracking-wide text-[#FF5722]">{d.role}</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-navy sm:text-6xl">
              <MaskedLines lines={d.heading_lines || []} lineClass="block" />
            </h2>
            <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-navy/60">{d.bio}</p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2">
              <div>
                <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-[#FF5722]">Responsibilities</p>
                <ul className="space-y-2">
                  {(d.responsibilities || []).map((r, i) => (
                    <li key={i} className="font-body text-sm text-navy/70">— {r}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-[#FF5722]">Approach</p>
                <ul className="space-y-2">
                  {(d.approach || []).map((r, i) => (
                    <li key={i} className="font-body text-sm font-semibold text-navy">{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* WHY ADMALL */
export function Why({ data }) {
  const d = data || {};
  return (
    <section id="why" data-testid="section-why" className="bg-white py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>Why ADMALL</SectionLabel>
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
          {(d.points || []).map((p, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="flex h-full items-center gap-4 bg-white p-8" data-testid={`why-point-${i}`}>
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5722] shrink-0" />
                <p className="font-display text-xl font-bold uppercase tracking-tight text-navy">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-12 text-center">
            {(d.statement_lines || []).map((l, i) => (
              <p key={i} className={`font-display text-3xl font-black uppercase tracking-tight sm:text-5xl ${i === 0 ? "text-navy/40" : "text-navy"}`}>{l}</p>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* WHAT WE DO NOT PROMISE */
export function NotPromise({ data }) {
  const d = data || {};
  return (
    <section id="not-promise" data-testid="section-not-promise" className="bg-[#0A0E1A] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel dark>Honesty</SectionLabel>
        <h2 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">{d.heading}</h2>
        <div className="mt-12 grid gap-14 lg:grid-cols-2">
          <div className="space-y-4">
            {(d.nots || []).map((n, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-center gap-4 border-b border-white/10 pb-4">
                  <X className="shrink-0 text-red-500" size={24} />
                  <p className="font-display text-2xl font-bold uppercase tracking-tight text-white/50 line-through decoration-red-500/60 sm:text-3xl">{n}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div>
            <Reveal>
              <p className="mb-6 font-display text-3xl font-black uppercase tracking-tight text-[#FF5722] sm:text-4xl">{d.promise_title}</p>
              <div className="space-y-3">
                {(d.promises || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="shrink-0 text-emerald-400" size={22} />
                    <p className="font-body text-lg text-white/90 sm:text-xl">{p}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* PROCESS */
export function Process({ data }) {
  const d = data || {};
  return (
    <section id="process" data-testid="section-process" className="bg-white py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>Process</SectionLabel>
        <h2 className="mb-14 font-display text-4xl font-extrabold uppercase tracking-tight text-navy sm:text-6xl">{d.heading}</h2>
        <div className="space-y-2">
          {(d.steps || []).map((st, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="relative border-b border-navy/10 py-7 flex items-start gap-4">
                <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-[#FF5722]" />
                <div>
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-navy sm:text-3xl">{st.title}</h3>
                  <p className="mt-2 font-body text-base text-navy/60 sm:text-lg max-w-3xl leading-relaxed">{st.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
